/**
 * AdminShell, wraps admin-mode routes in the Foundation shell
 * (components/AppShell.tsx, lane 'admin'; PLAT-364).
 *
 * Per D-087: admin mode is gated to role IN ('admin','owner'). Standard
 * users redirected to /home (they shouldn't reach an admin route URL
 * directly, but if they do, we send them back to user mode).
 *
 * The shell's top bar has the "Back to App" link → returns to user mode
 * at /home.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { pageScrollY, scrollStorageKey } from '../hooks/useScrollToTop.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { AppShell } from './AppShell.tsx';
import { BrandedLoader } from './BrandedLoader.tsx';
import { EmailVerificationBanner } from './EmailVerificationBanner.tsx';
import { LowBalanceBanner } from './LowBalanceBanner.tsx';
import { OnboardingIncompleteBanner } from './OnboardingIncompleteBanner.tsx';

export function AdminShell() {
  const { status, user } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return <BrandedLoader size="lg" message="Loading admin…" />;
  }
  if (status === 'unauthenticated' || status === 'error' || user === null) {
    // Sub-agent HHHHH (2026-07-29) — protected-page session probe.
    // See UserShell.tsx for the full framing. Same envelope: any failure
    // on /api/me → redirect to /sign-in?expired=1 with a flash message,
    // no user click required.
    //
    // Sub-agent IIIII (2026-07-29) — Track 5: preserve `?next=` so admin
    // users land back on the exact /admin/* page they were on. SignIn's
    // safeNextPath allowlist already covers /admin/*, so this dovetails
    // cleanly.
    const currentPath = location.pathname + location.search;
    // Sub-agent LLLLL (2026-07-29) — anti-punt for Push 102 Track 5 scroll
    // preservation. Same shape as UserShell: stash scrollY so useScrollToTop
    // can restore it on the /admin/* mount that follows sign-in. PLAT-378:
    // pageScrollY() is the shell's <main>'s offset while one is registered.
    const scrollY = typeof window !== 'undefined' ? pageScrollY() : 0;
    if (scrollY > 0) {
      try {
        sessionStorage.setItem(scrollStorageKey(currentPath), String(scrollY));
      } catch {
        /* private browsing / disabled storage — no-op */
      }
    }
    const target = `/sign-in?expired=1&next=${encodeURIComponent(currentPath)}`;
    return <Navigate to={target} replace />;
  }

  // Role gate, only admin + owner can reach admin mode.
  if (user.role !== 'admin' && user.role !== 'owner') {
    return <Navigate to="/home" replace />;
  }

  // Round 14 Item B: no FooterChrome on authenticated shells. The shell's
  // "Powered by Olytic" (shown when a brand logo is uploaded) keeps the
  // platform nod through a workspace rebrand.
  return (
    <AppShell
      lane="admin"
      banner={
        <>
          {/* Phase 1.21 (2026-06-29): persistent verify-email banner. Same
              mount semantics as UserShell. self-hides when verified. */}
          <EmailVerificationBanner />
          {/* Phase 1.32 (2026-06-29): safety-net banner for owners who landed
              on /admin/* without finishing setup. Renders nothing for
              completed owners or non-owner roles. */}
          <OnboardingIncompleteBanner />
          {/* Push 4-1ah (2026-07-10): low-balance banner (account pool < 500
              credits for admin/owner). Dismissible per-session. */}
          <LowBalanceBanner />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}
